import Utils from '@livingmap/utils';
import {LayerSpecification, CustomLayerInterface} from 'mapbox-gl'
import type LivingMap from '../map';
import { MapExpression } from './expressions';
import { GlobalFilters, LocalFilterOptions } from './types';

const NotHydratedError = new Error(
  'Filtering instance not hydrated, please ensure you wait for the style loading to be complete, and then call hydrate() (again)'
);

/**
 * This class aims to control and maintain all the mapbox filters on the layers.
 *
 * Filters control which features a style layer renders from its source.
 * Any feature for which the filter expression evaluates to true will be rendered on the map. Those that are false will be hidden.
 *
 * So a filter can be usefull to hide or show additional information without messing with the layer definitions too much.
 *
 * A filter could be usefull for:
 * - filtering data from a differtent z-index (building floors for example)
 * - category selection
 * ...
 *
 * The FilterKing must, before useage, store and maintain the existing filters on the layers.
 * Otherwise it will overwrite them with the newly added filters, rather than amending the existing filter.
 *
 * Furthermore, we make a destiction between `local` and `global` filters. A local filter ONLY affects a single layer,
 * whereas a global filter will affect ALL layers.
 */
class FilterKing {
  private global: { [key: string]: MapExpression } = {};
  private globalExlusions: { [key: string]: GlobalFilters[] | null | undefined } = {};
  private local: { [key: string]: MapExpression } = {};
  private preExistingStylesheetFilters: { [key: string]: MapExpression } = {};
  private debouncedApplyGlobal: () => void;

  private shouldValidateFilters: boolean;
  private hydrated = false;
  private LMMap: LivingMap;

  /**
   *
   * @param {LivingMap} LMMap {@link LivingMap} instance
   * @param {boolean} shouldValidateFilters validates filters before adding them to the internal map instance (set false for better performance but no validation)
   */
  constructor(LMMap: LivingMap, shouldValidateFilters = true) {
    this.shouldValidateFilters = shouldValidateFilters;
    this.LMMap = LMMap;
    this.debouncedApplyGlobal = Utils.debounce(this.applyGlobal, 100, false);
  }

  /**
   * This is a required function to call before accessing any other methods. An existing stylesheet must be initialized,
   * and all filters of that stylesheet must be available.
   *
   * On a new stylesheet load, either calling this again, or creating a new FilterKing instance would be required.
   *
   * @returns void
   */
  public hydrate(): void {
    const mapInstance = this.LMMap.getMapboxMap();
    const layers = mapInstance.getStyle()?.layers as (LayerSpecification | CustomLayerInterface)[] | undefined;
    if (!layers) throw NotHydratedError;

    layers.forEach((layer) => {
      if (layer.type !== 'custom') {
        this.preExistingStylesheetFilters[layer.id] = layer.filter as MapExpression;
      }
    });
    this.hydrated = true;
  }

  /**
   * Adds a local filter to a specific layerid, and immediately applies them to the layer at hand.
   * @param  {string} layerId
   * @param  {LocalFilterOptions} options
   * @returns void
   */
  public updateLocalFilter(layerId: string, options: LocalFilterOptions): void {
    const opts: LocalFilterOptions = { ...options };

    if (opts.filter) this.local[layerId] = opts.filter;
    if (opts.globalExclusions) this.globalExlusions[layerId] = opts.globalExclusions;

    this.applyFilterForLayer(layerId);
  }

  /**
   * Removes a local filters and global exclusions from selected layers and applies changes to map
   * @param  {string} layerId
   * @returns void
   */
  public removeLocalFilterForFilterId(layerId: string): void {
    if (!this.hydrated) throw NotHydratedError;

    delete this.local[layerId];
    delete this.globalExlusions[layerId];
    this.applyFilterForLayer(layerId);
  }

  /**
   * Adds a global filter to all layers, and will set off a debounce of a 100ms after which it will be applied to the map.
   * @param  {GlobalFilters} filterId
   * @param  {MapExpression} filter
   * @returns void
   */
  public updateGlobalFilter(filterId: GlobalFilters, filter: MapExpression): void {
    if (!this.hydrated) throw NotHydratedError;

    const filterString = filterId as string;
    this.global[filterString] = filter;
    this.debouncedApplyGlobal();
  }

  /**
   * Removes a global filter frkm all layers, and will set off a debounce of a 100ms after which it will be applied to the map.
   * @param  {GlobalFilters} filterId
   * @returns void
   */
  public removeGlobalFilterForFilterId(filterId: GlobalFilters): void {
    if (!this.hydrated) throw NotHydratedError;

    const filterString: string = filterId as string;
    delete this.global[filterString];
    this.debouncedApplyGlobal();
  }

  private applyGlobal = (): void => {
    if (!this.hydrated) throw NotHydratedError;

    const mapInstance = this.LMMap.getMapboxMap();
    const layers = mapInstance.getStyle()?.layers;
    if (!layers) return;

    layers.forEach((layer) => this.applyFilterForLayer(layer.id));
  };

  private applyFilterForLayer(layerId: string): void {
    // Applying filters before the map is ready is dangerous. We might loose any stylesheet filter
    // which resulted in inconsistent filtering behaviour.
    if (!this.hydrated) throw NotHydratedError;

    const filterForLayer = this.mergeFilters(layerId);

    // if the filter is empty no need to apply it!
    if (filterForLayer.length === 0) return;
    const mapInstance = this.LMMap.getMapboxMap();
    try{
      mapInstance.setFilter(layerId, filterForLayer, { validate: this.shouldValidateFilters });
    }catch(err){
      console.warn(err);
    }
  }

  private mergeFilters(layerId: string): MapExpression {
    const filter: MapExpression | unknown[] = [];
    const programmaticFilters = [];

    // when rebuilding a new filter we want to make sure any stylesheet based filtering happens first!
    const stylesheetFilters = this.preExistingStylesheetFilters[layerId];
    if (stylesheetFilters) {
      filter.push(stylesheetFilters);
    }

    const globalExclusionsForLayer = this.globalExlusions[layerId];
    const globalFilters = Object.keys(this.global)
      .filter((globalKey) => {
        if (!globalExclusionsForLayer) return true;
        return !globalExclusionsForLayer.includes(globalKey as GlobalFilters);
      })
      .map((key) => this.global[key]);

    if (globalFilters.length > 0) {
      programmaticFilters.push(...globalFilters);
    }

    const localFilters = this.local[layerId];
    if (localFilters) programmaticFilters.push(...localFilters);

    // if there are filters for this layer, we want to preempt them by saying,
    // ALL filters need to match before we all it to show.
    if (programmaticFilters.length > 0) {
      programmaticFilters.unshift('all');
      filter.push(programmaticFilters);
    }

    // if there are filters for this layer we want to ensure ALL should resolve to true before layer is shown.
    if (filter.length > 0) filter.unshift('all');

    return filter as MapExpression;
  }
}

export default FilterKing;
